/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import { formatNumber } from '@/utils/translationHelper';
import type { IconId } from '@/components/shared/Icons';
import type { Locale } from '@/i18n';

export type VenueHighlightStrings = {
  _meeting_rooms: string;
  _largest_meeting_room: string;
  _guest_rooms: string;
  _total_space: string;
  _sq_mtr_label: string;
  _sq_ft_label: string;
  _total_hotels: string;
  _total_restaurants: string;
  _largest_convention_space: string;
  _seating_capacity: string;
  _standing_capacity: string;
  _guest_rooms_at_largest_hotel: string;
  _venue_card_venue_highlights: string;
};

export type HighlightsProps = {
  highlightName: string;
  highlightValue: string;
  highlightIcon: IconId;
  title?: string;
};

const allHighlights = {
  MEETING_ROOMS: 'MEEETING_ROOMS',
  LARGEST_ROOM: 'LARGEST_ROOM',
  GUEST_ROOMS: 'GUEST_ROOMS',
  TOTAL_SPACE: 'TOTAL_SPACE',
  TOTAL_HOTELS: 'TOTAL_HOTELS',
  TOTAL_RESTAURANTS: 'TOTAL_RESTAURANTS',
  LARGEST_CONVENTION_SPACE: 'LARGEST_CONVENTION_SPACE',
  SEATING_CAPACITY: 'SEATING_CAPACITY',
  STANDING_CAPACITY: 'STANDING_CAPACITY',
  GUEST_ROOMS_AT_A_HOTEL: 'GUEST_ROOMS_AT_A_HOTEL'
} as const;

type HighlightStrings = {
  _meeting_rooms: string;
  _largest_meeting_room: string;
  _guest_rooms: string;
  _total_space: string;
  _sq_mtr_label: string;
  _sq_ft_label: string;
  _total_hotels: string;
  _total_restaurants: string;
  _largest_convention_space: string;
  _seating_capacity: string;
  _standing_capacity: string;
  _guest_rooms_at_largest_hotel: string;
};

// nishant - need types
function getHighlightsMap(
  value: number | undefined,
  isMetric: boolean,
  localeStrings: HighlightStrings,
  locale: Locale
): Map<string, HighlightsProps> {
  return new Map([
    [
      allHighlights.MEETING_ROOMS,
      {
        highlightName: localeStrings._meeting_rooms,
        highlightValue: formatNumber(locale, value ?? 0),
        highlightIcon: 'office-chair-custom'
      }
    ],
    [
      allHighlights.LARGEST_ROOM,
      {
        highlightName: localeStrings._largest_meeting_room,
        highlightValue: `${formatNumber(locale, Math.round(value ?? 0))} ${
          isMetric ? localeStrings._sq_mtr_label : localeStrings._sq_ft_label
        }`,
        highlightIcon: 'floorplan-custom'
      }
    ],
    [
      allHighlights.GUEST_ROOMS,
      {
        highlightName: localeStrings._guest_rooms,
        highlightValue: formatNumber(locale, value ?? 0),
        highlightIcon: 'bed-custom'
      }
    ],
    [
      allHighlights.TOTAL_SPACE,
      {
        highlightName: localeStrings._total_space,
        highlightValue: `${formatNumber(locale, Math.round(value ?? 0))} ${
          isMetric ? localeStrings._sq_mtr_label : localeStrings._sq_ft_label
        }`,
        highlightIcon: 'canvas-ruler-custom'
      }
    ],
    [
      allHighlights.TOTAL_HOTELS,
      {
        highlightName: localeStrings._total_hotels,
        highlightValue: formatNumber(locale, value ?? 0),
        highlightIcon: 'hotel'
      }
    ],
    [
      allHighlights.TOTAL_RESTAURANTS,
      {
        highlightName: localeStrings._total_restaurants,
        highlightValue: formatNumber(locale, value ?? 0),
        highlightIcon: 'utencils'
      }
    ],
    [
      allHighlights.LARGEST_CONVENTION_SPACE,
      {
        highlightName: localeStrings._largest_convention_space,
        highlightValue: `${formatNumber(locale, Math.round(value ?? 0))} ${
          isMetric ? localeStrings._sq_mtr_label : localeStrings._sq_ft_label
        }`,
        highlightIcon: 'canvas-ruler'
      }
    ],
    [
      allHighlights.SEATING_CAPACITY,
      {
        highlightName: localeStrings._seating_capacity,
        highlightValue: formatNumber(locale, value ?? 0),
        highlightIcon: 'office-chair'
      }
    ],
    [
      allHighlights.STANDING_CAPACITY,
      {
        highlightName: localeStrings._standing_capacity,
        highlightValue: formatNumber(locale, value ?? 0),
        highlightIcon: 'people-group'
      }
    ],
    [
      allHighlights.GUEST_ROOMS_AT_A_HOTEL,
      {
        highlightName: localeStrings._guest_rooms_at_largest_hotel,
        highlightValue: formatNumber(locale, value ?? 0),
        highlightIcon: 'bed'
      }
    ]
  ]);
}

/**
 * Hotel Highlights:
 *  - Meeting Rooms
 *  - Largest Room
 *  - Guest Rooms
 *  - Total space
 */
export function getDefaultVenueHighlights(
  locale: Locale,
  venue: CsnSearchVenue,
  measurementUnit: string,
  localeStrings: VenueHighlightStrings
): HighlightsProps[] {
  const isMetric = measurementUnit === 'METRIC';
  const highlights: HighlightsProps[] = [];
  if (venue.totalMeetingRoom) {
    highlights.push(
      getHighlightsMap(venue.totalMeetingRoom, isMetric, localeStrings, locale).get(allHighlights.MEETING_ROOMS)!
    );
  }
  if (venue.totalSleepingRoom) {
    highlights.push(
      getHighlightsMap(venue.totalSleepingRoom, isMetric, localeStrings, locale).get(allHighlights.GUEST_ROOMS)!
    );
  }

  if (venue.largestMeetingSpace?.imperialValue) {
    highlights.push(
      getHighlightsMap(
        isMetric ? venue.largestMeetingSpace?.metricValue : venue.largestMeetingSpace?.imperialValue,
        isMetric,
        localeStrings,
        locale
      ).get(allHighlights.LARGEST_ROOM)!
    );
  }

  if (venue.totalRoomSpaceArea?.imperialValue) {
    highlights.push(
      getHighlightsMap(
        isMetric ? venue.totalRoomSpaceArea?.metricValue : venue.totalRoomSpaceArea?.imperialValue,
        isMetric,
        localeStrings,
        locale
      ).get(allHighlights.TOTAL_SPACE)!
    );
  }
  return highlights;
}

/**
 * Restaurant Highlights:
 *  - Seating capacity
 *  - Standing capacity
 */
export function getRestaurantHighlights(
  locale: Locale,
  venue: CsnSearchVenue,
  measurementUnit: string,
  localeStrings: VenueHighlightStrings
): HighlightsProps[] {
  const isMetric = measurementUnit === 'METRIC';
  const highlights: HighlightsProps[] = [];
  if (venue.maxCapacitySeated) {
    highlights.push(
      getHighlightsMap(venue.maxCapacitySeated, isMetric, localeStrings, locale).get(allHighlights.SEATING_CAPACITY)!
    );
  }
  if (venue.maxCapacityStanding) {
    highlights.push(
      getHighlightsMap(venue.maxCapacityStanding, isMetric, localeStrings, locale).get(allHighlights.STANDING_CAPACITY)!
    );
  }
  return highlights;
}

/**
 * SEV Highlights:
 *  - Guest Rooms
 *  - Total space
 *  - Meeting rooms
 *  - Largest room
 */
export function getSEVHighlights(
  locale: Locale,
  venue: CsnSearchVenue,
  measurementUnit: string,
  localeStrings: VenueHighlightStrings
): HighlightsProps[] {
  const isMetric = measurementUnit === 'METRIC';
  const highlights: HighlightsProps[] = [];

  if (venue.totalMeetingRoom) {
    highlights.push(
      getHighlightsMap(venue.totalMeetingRoom, isMetric, localeStrings, locale).get(allHighlights.MEETING_ROOMS)!
    );

    if (venue.totalSleepingRoom) {
      highlights.push(
        getHighlightsMap(venue.totalSleepingRoom, isMetric, localeStrings, locale).get(allHighlights.GUEST_ROOMS)!
      );
    }
    if (venue.largestMeetingSpace?.imperialValue) {
      highlights.push(
        getHighlightsMap(
          isMetric ? venue.largestMeetingSpace?.metricValue : venue.largestMeetingSpace?.imperialValue,
          isMetric,
          localeStrings,
          locale
        ).get(allHighlights.LARGEST_ROOM)!
      );
    }
    if (venue.totalRoomSpaceArea?.imperialValue) {
      highlights.push(
        getHighlightsMap(
          isMetric ? venue.totalRoomSpaceArea?.metricValue : venue.totalRoomSpaceArea?.imperialValue,
          isMetric,
          localeStrings,
          locale
        ).get(allHighlights.TOTAL_SPACE)!
      );
    }
  }

  return highlights;
}

/**
 * CVB Highlights:
 *  - Total hotels
 *  - Total restaurants
 *  - Guest rooms at largest hotel
 *  - Largest convention space
 * DMC Highlights
 *  - Total hotels
 *  - Total restaurants
 *  - Guest rooms at largest hotel
 *  - Largest convention space
 */
export function getDMCOrCVBHighlights(
  locale: Locale,
  venue: CsnSearchVenue,
  measurementUnit: string,
  localeStrings: VenueHighlightStrings
): HighlightsProps[] {
  const isMetric = measurementUnit === 'METRIC';
  const highlights: HighlightsProps[] = [];
  if (venue.numberOfHotels) {
    highlights.push(
      getHighlightsMap(venue.numberOfHotels, isMetric, localeStrings, locale).get(allHighlights.TOTAL_HOTELS)!
    );
  }
  if (venue.numberOfRestaurants) {
    highlights.push(
      getHighlightsMap(venue.numberOfRestaurants, isMetric, localeStrings, locale).get(allHighlights.TOTAL_RESTAURANTS)!
    );
  }
  if (venue.numberOfCommittableSleepingRooms) {
    highlights.push(
      getHighlightsMap(venue.numberOfCommittableSleepingRooms, isMetric, localeStrings, locale).get(
        allHighlights.GUEST_ROOMS_AT_A_HOTEL
      )!
    );
  }
  if (venue.conventionCenterSpaceArea?.imperialValue) {
    highlights.push(
      getHighlightsMap(
        isMetric ? venue.conventionCenterSpaceArea?.metricValue : venue.conventionCenterSpaceArea?.imperialValue,
        isMetric,
        localeStrings,
        locale
      ).get(allHighlights.LARGEST_CONVENTION_SPACE)!
    );
  }
  return highlights;
}

/**
 * Convention Center Highlights
 *  - Guest rooms
 *  - Total space
 *  - Meeting rooms
 *  - Largest room
 */
export function getConventionCenterHighlights(
  locale: Locale,
  venue: CsnSearchVenue,
  measurementUnit: string,
  localeStrings: VenueHighlightStrings
): HighlightsProps[] {
  const isMetric = measurementUnit === 'METRIC';
  const highlights: HighlightsProps[] = [];
  if (venue.totalSleepingRoom) {
    highlights.push(
      getHighlightsMap(venue.totalSleepingRoom, isMetric, localeStrings, locale).get(allHighlights.GUEST_ROOMS)!
    );
  }
  if (venue.totalRoomSpaceArea?.imperialValue) {
    highlights.push(
      getHighlightsMap(
        isMetric ? venue.totalRoomSpaceArea?.metricValue : venue.totalRoomSpaceArea?.imperialValue,
        isMetric,
        localeStrings,
        locale
      ).get(allHighlights.TOTAL_SPACE)!
    );
  }
  if (venue.totalMeetingRoom) {
    highlights.push(
      getHighlightsMap(venue.totalMeetingRoom, isMetric, localeStrings, locale).get(allHighlights.MEETING_ROOMS)!
    );
  }
  if (venue.largestMeetingSpace?.imperialValue) {
    highlights.push(
      getHighlightsMap(
        isMetric ? venue.largestMeetingSpace?.metricValue : venue.largestMeetingSpace?.imperialValue,
        isMetric,
        localeStrings,
        locale
      ).get(allHighlights.LARGEST_ROOM)!
    );
  }
  return highlights;
}
